import React, { useState, useEffect, useCallback } from "react";
import {
  FiActivity,
  FiRefreshCw,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiPlay,
  FiCheckCircle,
  FiSliders,
  FiAward,
  FiShield,
  FiCreditCard,
  FiRotateCcw,
  FiUser,
  FiSlash,
  FiUnlock,
} from "react-icons/fi";
import toast from "react-hot-toast";

import { fetchEventActivity } from "../../../utils/eventApi";

/**
 * Everything an admin has done to one event, newest first.
 *
 * The question this answers is never "what does the event look like now" — the
 * rest of the page says that. It is "who moved the end date", "who approved this
 * payout", "why is this winner back to pending". So each line leads with the
 * sentence written at the time and the person who wrote it; the machine-readable
 * detail sits underneath for the rows where it adds something.
 */

/** A look per kind of action, so a long history is scannable rather than read. */
const ACTION_STYLE = {
  "event.create": { icon: FiPlus, tone: "bg-blue-50 text-blue-600", label: "Created" },
  "event.edit": { icon: FiEdit2, tone: "bg-gray-100 text-gray-600", label: "Edited" },
  "event.delete": { icon: FiTrash2, tone: "bg-rose-50 text-rose-600", label: "Deleted" },
  "event.publish": { icon: FiPlay, tone: "bg-emerald-50 text-emerald-600", label: "Published" },
  "event.status": { icon: FiActivity, tone: "bg-violet-50 text-violet-600", label: "Status" },
  "event.finalize": { icon: FiCheckCircle, tone: "bg-blue-50 text-blue-600", label: "Finalized" },
  "ranks.recompute": { icon: FiSliders, tone: "bg-indigo-50 text-indigo-600", label: "Ranks" },
  "points.adjust": { icon: FiSliders, tone: "bg-amber-50 text-amber-700", label: "Points" },
  "prize.create": { icon: FiAward, tone: "bg-amber-50 text-amber-700", label: "Prize" },
  "prize.update": { icon: FiAward, tone: "bg-amber-50 text-amber-700", label: "Prize" },
  "prize.delete": { icon: FiAward, tone: "bg-rose-50 text-rose-600", label: "Prize" },
  "sponsor.create": { icon: FiShield, tone: "bg-gray-100 text-gray-600", label: "Sponsor" },
  "sponsor.update": { icon: FiShield, tone: "bg-gray-100 text-gray-600", label: "Sponsor" },
  "sponsor.delete": { icon: FiShield, tone: "bg-rose-50 text-rose-600", label: "Sponsor" },
  "payout.approve": { icon: FiCreditCard, tone: "bg-emerald-50 text-emerald-600", label: "Approved" },
  "payout.revert": { icon: FiRotateCcw, tone: "bg-rose-50 text-rose-600", label: "Reverted" },
  "user.block": { icon: FiSlash, tone: "bg-red-50 text-red-600", label: "Blocked" },
  "user.unblock": { icon: FiUnlock, tone: "bg-green-50 text-green-600", label: "Unblocked" },
};

const FALLBACK = { icon: FiActivity, tone: "bg-gray-100 text-gray-500", label: "Activity" };

const FILTERS = [
  { key: "", label: "Everything" },
  { key: "payout.approve", label: "Approvals" },
  { key: "payout.revert", label: "Reverts" },
  { key: "event.edit", label: "Edits" },
  { key: "points.adjust", label: "Point changes" },
  { key: "user.block", label: "Blocks" },
  { key: "user.unblock", label: "Unblocks" },
];

const formatWhen = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime())
    ? "—"
    : d
        .toLocaleString("en-GB", {
          timeZone: "UTC",
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
        .replace(",", "") + " UTC";
};

/**
 * The `{before, after}` map an edit records, as readable lines.
 *
 * Anything else in `meta` is left to the raw view — inventing a rendering for
 * every shape would go stale the first time a new action is logged.
 */
const changeLines = (meta) => {
  if (!meta || typeof meta !== "object" || !meta.changes) return [];
  return Object.entries(meta.changes).map(([field, { before, after }]) => ({
    field,
    before: before === null || before === "" ? "—" : String(before),
    after: after === null || after === "" ? "—" : String(after),
  }));
};

const ActivityLog = ({ eventId }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const res = await fetchEventActivity(eventId, { page, limit: 50, action });
      if (res && res.status) {
        setEntries(res.data || []);
        setTotalPages(res.pagination?.total_pages || 1);
        setTotal(res.pagination?.total || 0);
      }
    } catch (err) {
      toast.error(err.message || "Failed to load the activity log");
    } finally {
      setLoading(false);
    }
  }, [eventId, page, action]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [action]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
            <FiActivity className="text-blue-600" /> Activity Log
          </h3>
          <p className="text-xs text-gray-400">
            Every admin action on this event, newest first{total ? ` · ${total} entries` : ""}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          >
            {FILTERS.map((f) => (
              <option key={f.key || "all"} value={f.key}>
                {f.label}
              </option>
            ))}
          </select>

          <button
            onClick={load}
            disabled={loading}
            className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 text-xs font-medium rounded-lg transition flex items-center gap-1.5"
          >
            <FiRefreshCw size={13} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {loading && entries.length === 0 ? (
        <div className="p-8 text-center text-xs text-gray-400 border border-gray-200 rounded-2xl">
          Loading activity…
        </div>
      ) : entries.length === 0 ? (
        <div className="p-8 border-2 border-dashed border-gray-200 rounded-2xl text-center text-xs text-gray-400">
          {action
            ? "Nothing matches this filter."
            : "No activity recorded yet. Actions taken from here on will appear."}
        </div>
      ) : (
        <ol className="relative border-l-2 border-gray-100 ml-3 space-y-1">
          {entries.map((entry) => {
            const style = ACTION_STYLE[entry.action] || FALLBACK;
            const Icon = style.icon;
            const lines = changeLines(entry.meta);
            const isOpen = expanded === entry.id;
            const hasDetail = lines.length > 0 || Boolean(entry.meta);

            return (
              <li key={entry.id} className="relative pl-8 py-2.5">
                <span
                  className={`absolute -left-[15px] top-3 w-7 h-7 rounded-full flex items-center justify-center ring-4 ring-white ${style.tone}`}
                >
                  <Icon size={13} />
                </span>

                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-1.5">
                  <div className="min-w-0">
                    <p className="text-xs text-gray-800">{entry.summary}</p>
                    <p className="text-[11px] text-gray-400 flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <span
                        className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${style.tone}`}
                      >
                        {style.label}
                      </span>
                      <span className="flex items-center gap-1">
                        <FiUser size={10} />
                        {entry.admin_name || entry.admin_email || (entry.admin_id ? `#${entry.admin_id}` : "system")}
                      </span>
                      <span>·</span>
                      <span className="font-mono">{formatWhen(entry.created_at)}</span>
                    </p>
                  </div>

                  {hasDetail && (
                    <button
                      onClick={() => setExpanded(isOpen ? null : entry.id)}
                      className="text-[11px] font-semibold text-blue-600 hover:text-blue-700 shrink-0"
                    >
                      {isOpen ? "Hide detail" : "Detail"}
                    </button>
                  )}
                </div>

                {isOpen && (
                  <div className="mt-2 p-2.5 bg-gray-50 border border-gray-100 rounded-xl">
                    {lines.length > 0 ? (
                      <table className="w-full text-[11px]">
                        <tbody>
                          {lines.map((line) => (
                            <tr key={line.field}>
                              <td className="pr-3 py-0.5 font-semibold text-gray-500 align-top">
                                {line.field}
                              </td>
                              <td className="py-0.5 text-gray-400 line-through break-all">{line.before}</td>
                              <td className="px-2 py-0.5 text-gray-300">→</td>
                              <td className="py-0.5 text-gray-700 font-medium break-all">{line.after}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <pre className="text-[10px] text-gray-500 whitespace-pre-wrap break-all">
                        {JSON.stringify(entry.meta, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs disabled:opacity-40 hover:bg-gray-50 transition"
            >
              Previous
            </button>
            <button
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs disabled:opacity-40 hover:bg-gray-50 transition"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityLog;
